import type { RouteLocationNormalized } from 'vue-router'

import { createRouter, createWebHistory } from 'vue-router'
import { handleHotUpdate, routes } from 'vue-router/auto-routes'

import { isStaleChunkError, reloadForStaleAssets } from '@/bootstrap/staleAssets'
import { appConfirm } from '@/composables/useAppConfirm'
import i18n from '@/i18n'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

if (import.meta.hot) {
  handleHotUpdate(router)
}

function previousRouteOrHome(from: RouteLocationNormalized) {
  return from.fullPath !== '/' ? from : '/'
}

router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  const { isAuthenticated, hasRole, hasInternalAccess } = useUser()
  const { adminModeEnabled } = useAdminMode()
  const { t } = i18n.i18n.global

  if (to.meta.requiresAuth && !isAuthenticated.value) {
    const accepted = await appConfirm({
      title: t('pages.user.login.dialog.title'),
      message: t('pages.user.login.dialog.message'),
      confirmLabel: t('pages.user.login.loginButton'),
      cancelLabel: t('common.actions.cancel'),
    })

    return accepted ? { path: '/user/login', query: { returnUrl: to.path } } : false
  }

  if (to.meta.requiresAdmin && !hasRole.value('admin')) {
    const acknowledged = await appConfirm({
      title: t('pages.user.login.accessDenied.title'),
      message: t('pages.user.login.accessDenied.message'),
      confirmLabel: t('common.labels.ok'),
      showCancel: false,
    })
    return acknowledged ? previousRouteOrHome(from) : false
  }

  if (to.meta.requiresAdminMode && (!hasRole.value('admin') || !adminModeEnabled.value)) {
    const acknowledged = await appConfirm({
      title: t('pages.user.login.accessDenied.title'),
      message: t('pages.user.login.accessDenied.message'),
      confirmLabel: t('common.labels.ok'),
      showCancel: false,
    })
    return acknowledged ? previousRouteOrHome(from) : false
  }

  if (to.meta.requiresInternalAccess && !hasInternalAccess.value) {
    const acknowledged = await appConfirm({
      title: t('pages.user.login.accessDenied.title'),
      message: t('pages.user.login.accessDenied.message'),
      confirmLabel: t('common.labels.ok'),
      showCancel: false,
    })
    return acknowledged ? previousRouteOrHome(from) : false
  }

  return true
})

// A route's component chunk is gone after a deploy while this tab was still open: reload straight
// to the page the user was opening instead of leaving the click dead.
router.onError((error, to) => {
  if (isStaleChunkError(error)) {
    reloadForStaleAssets(to.fullPath)
  }
})

export default router
